import { apiRequest } from "./api";
import type { EnvelopeResponse } from "../types/auth";
import type {
  WhatsAppSession,
  WhatsAppSessionSerializerDocBody,
  ConnectSessionRequest,
  ConnectSessionResponse,
  AssignChatRequest,
  AssignContactoRequest,
  EnviarMensajeRequest,
  Chat,
  Contacto,
  Mensaje,
  ConnectionStatusResponse,
  SessionStatusResponse,
  EnvelopeData,
  PaginatedResults,
} from "../types/whatsapp";

// Helper to extract data from Envelope when needed
function extractEnvelopeData<T>(response: EnvelopeData<T> | T): T {
  if (response && typeof response === "object" && "code" in response && "data" in response) {
    return (response as EnvelopeData<T>).data;
  }
  return response as T;
}

// Helper to extract results from paginated envelope
function extractPaginated<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === "object") {
    const r = response as Record<string, unknown>;
    if ("results" in r && Array.isArray(r.results)) return r.results as T[];
    if ("data" in r && typeof r.data === "object" && r.data !== null) {
      const d = r.data as Record<string, unknown>;
      if ("results" in d && Array.isArray(d.results)) return d.results as T[];
    }
  }
  return [];
}

// ========== WHATSAPP SESSIONS ==========

export async function listSessions(): Promise<WhatsAppSession[]> {
  const response = await apiRequest<WhatsAppSession[] | EnvelopeData<WhatsAppSession[]>>("/api/v1/crmwhatsapp/whats-app-session/");
  return extractPaginated(response);
}

export async function createSession(data: WhatsAppSessionSerializerDocBody): Promise<WhatsAppSession> {
  return apiRequest<WhatsAppSession>("/api/v1/crmwhatsapp/whats-app-session/", {
    method: "POST",
    body: data,
  });
}

export async function getSession(id: string): Promise<WhatsAppSession> {
  return apiRequest<WhatsAppSession>(`/api/v1/crmwhatsapp/whats-app-session/${id}/`);
}

export async function updateSession(id: string, data: WhatsAppSessionSerializerDocBody): Promise<WhatsAppSession> {
  return apiRequest<WhatsAppSession>(`/api/v1/crmwhatsapp/whats-app-session/${id}/`, {
    method: "PUT",
    body: data,
  });
}

export async function deleteSession(id: string): Promise<WhatsAppSession> {
  return apiRequest<WhatsAppSession>(`/api/v1/crmwhatsapp/whats-app-session/${id}/`, {
    method: "DELETE",
  });
}

// ========== ROLE-BASED ENDPOINTS ==========

export async function connectSessionWithRole(data: ConnectSessionRequest): Promise<ConnectSessionResponse> {
  return apiRequest<ConnectSessionResponse>("/api/v1/crmwhatsapp/sesiones/conectar/", {
    method: "POST",
    body: data,
  });
}

export async function listSessionsByPhone(phone: string): Promise<WhatsAppSession[]> {
  return apiRequest<WhatsAppSession[]>(
    `/api/v1/crmwhatsapp/sesiones/por-telefono/?phone=${encodeURIComponent(phone)}`
  );
}

export async function assignChatToRole(data: AssignChatRequest): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/crmwhatsapp/sesiones/asignar-chat/", {
    method: "POST",
    body: data,
  });
}

export async function assignContactoToRole(data: AssignContactoRequest): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/crmwhatsapp/sesiones/asignar-contacto/", {
    method: "POST",
    body: data,
  });
}

export async function sendMessageFromRole(data: EnviarMensajeRequest): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/crmwhatsapp/sesiones/enviar-mensaje/", {
    method: "POST",
    body: data,
  });
}

// ========== SESSION MANAGEMENT ==========

/** POST /sesiones/{session_id}/logout/ - Cierra sesión WhatsApp completamente */
export async function logoutSession(sessionId: string): Promise<unknown> {
  return apiRequest<unknown>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/logout/`, {
    method: "POST",
  });
}

/** GET /sesiones/{session_id}/estado/ - Consulta estado para polling */
export async function getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
  return apiRequest<SessionStatusResponse>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/estado/`);
}

/** GET /sesiones/{session_id}/qr/ - Obtener QR individual */
export async function getSessionQR(sessionId: string): Promise<{ qr_data_url: string }> {
  return apiRequest<{ qr_data_url: string }>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/qr/`);
}

/** POST /sesiones/{session_id}/detener/ - Detener socket */
export async function stopSession(sessionId: string): Promise<unknown> {
  return apiRequest<unknown>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/detener/`, {
    method: "POST",
  });
}

/** POST /sesiones/{session_id}/reiniciar/ - Reiniciar socket */
export async function restartSession(sessionId: string): Promise<unknown> {
  return apiRequest<unknown>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/reiniciar/`, {
    method: "POST",
  });
}

/** DELETE /sesiones/{session_id}/eliminar-sesion/ - Eliminar sesión (admin) */
export async function deleteSessionRecord(sessionId: string): Promise<unknown> {
  return apiRequest<unknown>(`/api/v1/crmwhatsapp/sesiones/${sessionId}/eliminar-sesion/`, {
    method: "DELETE",
  });
}

// ========== CONNECTION STATUS ==========

/** GET /sesiones/estado-conexion/ - Verificar si hay sesión conectada */
export async function getConnectionStatus(): Promise<ConnectionStatusResponse> {
  return apiRequest<ConnectionStatusResponse>("/api/v1/crmwhatsapp/sesiones/estado-conexion/");
}

// ========== VERIFY ACCESS ==========

export async function checkAccess(chatId: string): Promise<EnvelopeResponse<{ allowed: boolean }>> {
  return apiRequest<EnvelopeResponse<{ allowed: boolean }>>(`/api/v1/crmwhatsapp/agentes/verificar-acceso/${chatId}/`);
}

// ========== AGENT ENDPOINTS ==========

export async function getMisChats(): Promise<Chat[]> {
  const response = await apiRequest<EnvelopeData<PaginatedResults<Chat>> | PaginatedResults<Chat> | Chat[]>(
    "/api/v1/crmwhatsapp/agentes/mis-chats/"
  );
  return extractPaginated(response);
}

export async function getBandejaGeneral(): Promise<Chat[]> {
  const response = await apiRequest<EnvelopeData<PaginatedResults<Chat>> | PaginatedResults<Chat> | Chat[]>(
    "/api/v1/crmwhatsapp/agentes/bandeja-general/"
  );
  return extractPaginated(response);
}

export async function getAgentesDisponibles(): Promise<{ id: number; username: string; email: string }[]> {
  const response = await apiRequest<
    EnvelopeData<PaginatedResults<{ id: number; username: string; email: string }>> | 
    { id: number; username: string; email: string }[]
  >("/api/v1/crmwhatsapp/agentes/disponibles/");
  return extractPaginated(response);
}

export async function reasignarChat(chatId: string, agenteId: number | null): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>(`/api/v1/crmwhatsapp/agentes/reasignar-chat/${chatId}/`, {
    method: "PATCH",
    body: { agente_id: agenteId },
  });
}

// ========== CHATS ==========

export async function listChats(params?: {
  page?: number;
  page_size?: number;
  kind?: string;
  is_group?: boolean;
  jid?: string;
  sesion_id?: string;
  sesion_rol?: string;
  sesion_phone?: string;
  agente_asignado_id?: number;
  agente_asignado_username?: string;
  sin_asignar?: boolean;
  ordering?: string;
}): Promise<Chat[]> {
  const response = await apiRequest<Chat[] | EnvelopeData<PaginatedResults<Chat>>>("/api/v1/crmwhatsapp/chats/", {
    params: params as Record<string, string | number | boolean | undefined>,
  });
  return extractPaginated(response);
}

export async function getChat(id: string): Promise<Chat> {
  return apiRequest<Chat>(`/api/v1/crmwhatsapp/chats/${id}/`);
}

// ========== CONTACTOS ==========

export async function listContactos(params?: {
  page?: number;
  page_size?: number;
  buscar?: string;
  phone?: string;
}): Promise<Contacto[]> {
  const response = await apiRequest<Contacto[] | EnvelopeData<PaginatedResults<Contacto>>>("/api/v1/crmwhatsapp/contactos/", {
    params: params as Record<string, string | number | boolean | undefined>,
  });
  return extractPaginated(response);
}

// ========== MENSAJES ==========

export async function listMensajes(params?: {
  page?: number;
  page_size?: number;
  chat?: string;
  contacto?: string;
  from_me?: boolean;
  sesion_id?: string;
  ordering?: string;
}): Promise<Mensaje[]> {
  const response = await apiRequest<Mensaje[] | EnvelopeData<PaginatedResults<Mensaje>>>("/api/v1/crmwhatsapp/mensajes/", {
    params: params as Record<string, string | number | boolean | undefined>,
  });
  return extractPaginated(response);
}